import { useEffect, useMemo, useState } from 'react';
import type { Role } from '@kia/shared';
import { api } from '../api/client';
import { rowButton } from '../lib/rowButton';
import { Card, Button } from '../ui';
import { Input } from '../ui/Field';

/**
 * راهنما و آموزش — the department training manual + the live assumption & calculation master.
 * Content comes from the server (`/api/help/manual`) so it is the SAME source the AI assistant answers
 * from and the global search indexes. Role-aware (opens on your department), fully searchable, and every
 * article can be handed to the assistant for a deeper "why / how".
 */

interface Article {
  id: string;
  dept: string;
  title: string;
  body: string;
  keywords: string;
  screen?: string;
}
interface Dept {
  key: string;
  label: string;
  blurb: string;
}
interface Assumption {
  key: string;
  category: string;
  label: string;
  value: string;
  description: string;
  editable: boolean;
  source: string;
}

/** Which department tab opens first for each role (mirror of the server's ROLE_DEPARTMENT). */
const ROLE_DEPT: Record<Role, string> = {
  Admin: 'admin',
  Editor: 'sales',
  Sales: 'sales',
  Purchasing: 'purchasing',
  Finance: 'finance',
  Warehouse: 'warehouse',
  Security: 'security',
  Viewer: 'general',
};

export function HelpScreen({
  role,
  focusHelp,
  onAskAi,
}: {
  role: Role;
  focusHelp: number | null;
  onAskAi: () => void;
}) {
  const [departments, setDepartments] = useState<Dept[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [assumptions, setAssumptions] = useState<Assumption[]>([]);
  const [dept, setDept] = useState<string>(ROLE_DEPT[role] ?? 'general');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    void api<{ departments: Dept[]; articles: Article[] }>('/api/help/manual').then((r) => {
      if (r.ok) {
        const b = r.body as { departments: Dept[]; articles: Article[] };
        setDepartments(b.departments);
        setArticles(b.articles);
      }
    });
    void api<{ assumptions: Assumption[] }>('/api/assumptions').then((r) => {
      if (r.ok) setAssumptions((r.body as { assumptions: Assumption[] }).assumptions);
    });
  }, []);

  // A global-search hit on a help article (its index) opens it and switches to its department.
  useEffect(() => {
    if (focusHelp == null || !articles.length) return;
    const a = articles[focusHelp];
    if (a) {
      setDept(a.dept);
      setQ('');
      setOpenId(a.id);
    }
  }, [focusHelp, articles]);

  const nq = q.trim().toLowerCase();
  const shown = useMemo(() => {
    if (nq) {
      // search spans EVERY department
      return articles.filter((a) =>
        `${a.title} ${a.keywords} ${a.body}`.toLowerCase().includes(nq),
      );
    }
    return articles.filter((a) => a.dept === dept);
  }, [articles, dept, nq]);

  const cats = [...new Set(assumptions.map((r) => r.category))];
  const deptLabel = (key: string) => departments.find((d) => d.key === key)?.label ?? key;

  return (
    <>
      <Card title="راهنما و آموزش واحدها">
        <p className="muted" style={{ margin: '0 0 10px', fontSize: 12, lineHeight: 1.8 }}>
          هر واحد راهنمای گام‌به‌گام خودش را دارد. برای مطالعه، واحد خود را انتخاب کنید یا در کادر
          زیر جستجو کنید (جستجو همهٔ واحدها را می‌گردد). همین مطالب در «جستجوی سراسری» و در «دستیار
          تحلیل» هم در دسترس است.
        </p>

        <Input
          placeholder="جستجو در راهنما… (مثلاً: تثبیت قیمت، تقویم نقدینگی، ترخیص)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ marginBottom: 10 }}
        />

        {!nq && (
          <div className="row" style={{ flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {departments.map((d) => (
              <Button
                key={d.key}
                variant={d.key === dept ? 'primary' : 'secondary'}
                onClick={() => {
                  setDept(d.key);
                  setOpenId(null);
                }}
              >
                {d.label}
              </Button>
            ))}
          </div>
        )}

        {!nq && (
          <p className="muted" style={{ margin: '0 0 10px', fontSize: 12 }}>
            {departments.find((d) => d.key === dept)?.blurb}
          </p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {shown.length === 0 ? (
            <span className="muted">موردی یافت نشد.</span>
          ) : (
            shown.map((a) => {
              const open = openId === a.id;
              return (
                <div
                  key={a.id}
                  style={{
                    border: '1px solid var(--line, #eee)',
                    borderRadius: 8,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    {...rowButton(() => setOpenId(open ? null : a.id))}
                    aria-expanded={open}
                    style={{
                      cursor: 'pointer',
                      padding: '10px 12px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 8,
                      background: open ? 'var(--panel, #fafafa)' : undefined,
                    }}
                  >
                    <span style={{ fontWeight: 700, color: 'var(--navy-l)' }}>{a.title}</span>
                    <span className="muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                      {nq ? deptLabel(a.dept) : ''} {open ? '▲' : '▼'}
                    </span>
                  </div>
                  {open && (
                    <div style={{ padding: '4px 12px 12px' }}>
                      {a.body.split('\n').map(
                        (para, i) =>
                          para.trim() && (
                            <p key={i} style={{ fontSize: 13, lineHeight: 1.9, margin: '6px 0' }}>
                              {para}
                            </p>
                          ),
                      )}
                      <Button variant="ghost" onClick={onAskAi} style={{ marginTop: 4 }}>
                        💬 پرسش بیشتر از دستیار
                      </Button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </Card>

      <Card title="فرض‌ها و مبنای محاسبات">
        <p className="muted" style={{ margin: '0 0 10px', fontSize: 12 }}>
          هر عدد و قاعده‌ای که سامانه بر آن اجرا می‌شود. مقادیر «قابل ویرایش» در «تنظیمات» تغییر
          می‌کنند؛ «قاعده»‌ها ثابت و مستند هستند. دستیار تحلیل هم بر همین مبنا پاسخ می‌دهد.
        </p>
        {cats.map((cat) => (
          <div key={cat} style={{ marginTop: 8 }}>
            <div style={{ fontWeight: 700, color: 'var(--navy-l)', margin: '8px 0 4px' }}>
              {cat}
            </div>
            <table className="table">
              <thead>
                <tr>
                  <th>مورد</th>
                  <th>مقدار</th>
                  <th>توضیح</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {assumptions
                  .filter((r) => r.category === cat)
                  .map((r) => (
                    <tr key={r.key}>
                      <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{r.label}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{r.value}</td>
                      <td style={{ fontSize: 12 }}>{r.description}</td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 11 }}>
                        {r.editable ? (
                          <span style={{ color: 'var(--green)' }}>قابل ویرایش</span>
                        ) : (
                          <span className="muted">قاعده</span>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ))}
      </Card>
    </>
  );
}
